// =============================
// Imports
// =============================

import { Component } from 'react';
import PropTypes from 'prop-types';
import _get from 'lodash/get';

import * as playerCtx from '../../../store/constants/PlayerContexts';

import EntityActions from '../../other/entityActions';
import Link from '../../other/link';
import MusicItem from '../../containers/musicitem';
import PageWrapper from '../../other/pagewrapper';

import { dateToString } from '../../../helpers/doc';
import { getArtistCoverUrl, getArtistName } from '../../../helpers/entity/artist';
import { getAlbumCoverUrl, getAlbumTitle } from '../../../helpers/entity/album';
import {
  getPlaylistCoverUrl,
  getPlaylistTitle,
  getPlaylistTracksCoversUrl,
} from '../../../helpers/entity/playlist';
import { getBaseRoute, getUrl } from '../../../helpers/entity';
import { presentShortDescription, isDescriptionTooLong } from '../../../helpers/entity/common';
import * as pth from '../../../helpers/proptypes';
import { getLinkRouteProps } from '../../../helpers/link';

import {
  Header,
  Title,
  Name,
  ListContent,
  ListFooter,
  StyledMusicItemOptionsToggler,
  SeeMoreButton,
  EntityDesc,
  EntityCover,
  AlbumCover,
  PlaylistCover,
  Informations,
  DescSeeMore,
  EntityItemList,
  Breadcrumb,
} from '../entity.styles';

// =============================
// Component
// =============================

class Artist extends Component {
  static propTypes = {
    albums: PropTypes.arrayOf(pth.album).isRequired,
    albumsAppearsOn: PropTypes.arrayOf(pth.album).isRequired,
    albumsTotal: PropTypes.number.isRequired,
    artist: pth.artist.isRequired,
    getAlbums: PropTypes.func.isRequired,
    hasMoreAlbums: PropTypes.bool.isRequired,
    locale: PropTypes.string.isRequired,
    openArtistDescModal: PropTypes.func.isRequired,
    playlistsAppearsOn: PropTypes.arrayOf(pth.playlist).isRequired,
    t: PropTypes.func.isRequired,
    tracks: PropTypes.arrayOf(pth.track).isRequired,
    parentPage: pth.baseConfigMenu,
  };

  static defaultProps = {
    parentPage: [],
  };

  render() {
    const {
      albums,
      albumsAppearsOn,
      albumsTotal,
      artist,
      getAlbums,
      hasMoreAlbums,
      locale,
      isLogged,
      openArtistDescModal,
      playlistsAppearsOn,
      t,
      tracks,
      parentPage,
    } = this.props;

    return (
      <PageWrapper type="entity">
        {parentPage[0] && (
          <Breadcrumb {...getLinkRouteProps(parentPage[0], isLogged)}>
            {t('pages:entity.all_artists')}
          </Breadcrumb>
        )}
        <Header>
          <EntityCover
            placeholderType="artist"
            type="artist"
            src={getArtistCoverUrl(artist, 'large')}
            lazyload={false}
            rounded
            alt={getArtistName(artist)}
          />
          <Informations>
            <Name>{getArtistName(artist)}</Name>

            {presentShortDescription(_get(artist, 'descriptions', []), locale) && (
              <EntityDesc>
                {presentShortDescription(_get(artist, 'descriptions', []), locale)}
                {isDescriptionTooLong(_get(artist, 'descriptions', []), locale) && (
                  <DescSeeMore onClick={openArtistDescModal}>
                    {t('pages:entity.see_more')}
                  </DescSeeMore>
                )}
              </EntityDesc>
            )}

            <EntityActions
              doc={artist}
              type="artist"
              options={{
                addToPlaylist: false,
                downloadArchive: false,
                share: true,
                duplicatePlaylist: false,
                downloadCopyright: false,
              }}
              hasTracks={!!tracks.length}
              playerContext={playerCtx.ARTIST}
            />
          </Informations>
        </Header>

        {tracks.length > 0 && (
          <EntityItemList trackList>
            <Title>
              {t('pages:entity.recent_tracks')}
              <StyledMusicItemOptionsToggler />
            </Title>
            {tracks.map((e, i) => (
              <MusicItem
                key={e.id}
                music={e}
                contextName={playerCtx.ARTIST}
                contextId={artist.id}
                contextPosition={i + 1}
                lazyload={false}
              />
            ))}
          </EntityItemList>
        )}

        {albumsTotal > 0 && (
          <EntityItemList hasMoreBtn={hasMoreAlbums}>
            <Title>
              {albumsTotal}
              &nbsp;
              {t('pages:entity.albums')}
            </Title>
            <ListContent goVerticalOnMobile>
              {albums.map(e => (
                <AlbumCover
                  alt={getAlbumTitle(e)}
                  key={e.id}
                  date={dateToString(e.releaseDate)}
                  src={getAlbumCoverUrl(e)}
                  title={getAlbumTitle(e)}
                  LinkComponent={Link}
                  linkProps={{
                    route: getBaseRoute('albums'),
                    nextAs: getUrl('albums')(e),
                  }}
                  contextName={playerCtx.ALBUM}
                  contextId={e.id}
                  lazyload={false}
                />
              ))}
            </ListContent>
            {hasMoreAlbums && (
              <ListFooter>
                <SeeMoreButton onClick={getAlbums}>{t('pages:entity.see_more')}</SeeMoreButton>
              </ListFooter>
            )}
          </EntityItemList>
        )}

        {albumsAppearsOn.length > 0 && (
          <EntityItemList>
            <Title>{t('pages:entity.appears_on_albums')}</Title>
            <ListContent goVerticalOnMobile>
              {albumsAppearsOn.map(e => (
                <AlbumCover
                  alt={getAlbumTitle(e)}
                  key={e.id}
                  date={dateToString(e.releaseDate)}
                  src={getAlbumCoverUrl(e)}
                  title={getAlbumTitle(e)}
                  LinkComponent={Link}
                  linkProps={{
                    route: getBaseRoute('albums'),
                    nextAs: getUrl('albums')(e),
                  }}
                  contextName={playerCtx.ALBUM}
                  contextId={e.id}
                  lazyload={false}
                />
              ))}
            </ListContent>
          </EntityItemList>
        )}

        {playlistsAppearsOn.length > 0 && (
          <EntityItemList>
            <Title>{t('pages:entity.appears_on_playlists')}</Title>
            <ListContent>
              {playlistsAppearsOn.map(e => (
                <PlaylistCover
                  key={e.id}
                  alt={getPlaylistTitle(e)}
                  title={getPlaylistTitle(e)}
                  description={presentShortDescription(e.descriptions, locale, 70)}
                  src={getPlaylistCoverUrl(e) || getPlaylistTracksCoversUrl(e)}
                  LinkComponent={Link}
                  linkProps={{
                    route: getBaseRoute('playlists'),
                    nextAs: getUrl('playlists')(e),
                  }}
                  contextName={playerCtx.PLAYLIST}
                  contextId={e.id}
                  lazyload={false}
                />
              ))}
            </ListContent>
          </EntityItemList>
        )}
      </PageWrapper>
    );
  }
}

export default Artist;
